import React from 'react';
const { updateSyncErrors, touch } = require('redux-form/lib/actions').default;
import get from 'lodash/get';
import {
    change, // The action creator
} from 'redux-form';

import { AlertChunky } from '@eventbrite/eds-iconography';
import { EmailsApi } from '@eventbrite/auth-lib';
import { PasswordlessApi } from '@eventbrite/auth-lib';

import {
    trackPasswordlessSuccess,
    trackPasswordlessLoginFailed,
    trackLoginSuccessful,
    trackPasswordlessEmailAttempt,
    trackPasswordlessEmailFailed,
    trackPasswordlessEmailSent,
    trackPasswordlessSetPasswordSuccess,
    trackPasswordlessSetPasswordFailed,
} from './gaTracking';

import {
    showAuthNotification,
    clearAuthNotification,
    redirectToSignUp,
    redirectToLogIn,
    setIsLoading,
    clearEmail,
    setAccountExistent,
} from './auth';

import { setSignupSecondStep } from './lookup';

import { GA_EVENTBRITE, GA_PASSWORDLESS } from '../constants/analytics';
import { PASSWORDLESS_EMAIL_STATUSES } from '../constants/status';
import {
    NOT_EMAIL_ASSOCIATED,
    CREATE_ACCOUNT,
    GO_TO_LOGIN,
    GENERIC_ERROR_MESSAGE,
    NOT_AUTHORIZED_ERROR_MESSAGE,
    SUSPENDED_ACCOUNT_ERROR_MESSAGE,
    TOS_PASSWORDLESS_MUST_BE_ACCEPTED,
    PASSWORDLESS_TOKEN_EXPIRED_MESSAGE,
    PASSWORDLESS_TOKEN_REUSED_MESSAGE,
    NEW_PASSWORD_TOO_WEAK_ERROR_MESSAGE,
    USER_HAS_ALREADY_PASSWORD_ERROR_MESSAGE,
    USER_ACCOUNT_SUSPENDED_ERROR_MESSAGE,
    INVALID_USER_ID_ERROR_MESSAGE,
    ENTER_VALID_EMAIL,
} from '../constants/messages';
import {
    LOG_IN_FORM,
    PASSWORDLESS_FORM,
    PASSWORDLESS_LOGIN_OK,
    PASSWORDLESS_LOGIN_NOT_AUTHORIZED,
    PASSWORDLESS_LOGIN_TOKEN_EXPIRED,
    PASSWORDLESS_LOGIN_TOKEN_REUSED,
    PASSWORDLESS_LOGIN_OTHER_ERROR,
    PASSWORDLESS_LOGIN_MUST_ACCEPT_TOS,
} from '../constants';
import { submitSuccessRedirect } from '../utils/security';
import {
    HeapTrackingLibrary,
    LOGIN_METHOD_PASSWORDLESS,
} from '@eventbrite/auth-lib/';

/**  ACTION CREATORS  **/
export const SET_PASSWORDLESS_EMAIL_STATUS = 'SET_PASSWORDLESS_EMAIL_STATUS';
export const setPasswordlessEmailStatus = (payload) => ({
    type: SET_PASSWORDLESS_EMAIL_STATUS,
    payload,
});

export const SET_PASSWORDLESS_LOGIN_STATUS = 'SET_PASSWORDLESS_LOGIN_STATUS';
export const setPasswordlessLoginStatus = (payload) => ({
    type: SET_PASSWORDLESS_LOGIN_STATUS,
    payload,
});

export const SET_SHOW_PASSWORDLESS_MODAL = 'SET_SHOW_PASSWORDLESS_MODAL';
export const setShowPasswordlessModal = (show) => ({
    type: SET_SHOW_PASSWORDLESS_MODAL,
    payload: show,
});

export const SET_PASSWORDLESS_CREATE_ACCOUNT_FLOW =
    'SET_PASSWORDLESS_CREATE_ACCOUNT_FLOW';
export const setPasswordlessCreateAccountFlow = (payload) => ({
    type: SET_PASSWORDLESS_CREATE_ACCOUNT_FLOW,
    payload,
});

export const PASSWORDLESS_ERROR_SPEC = {
    EMAIL_INVALID: {
        email: ENTER_VALID_EMAIL,
    },
    EMAIL_CREATE_ACCOUNT: {
        email: ' ', // No display error text, only red border.
    },
};

const redirectToSignUpWithLabel = () => (dispatch, getState) => {
    dispatch(setShowPasswordlessModal(false));
    dispatch(setPasswordlessCreateAccountFlow(true));
    dispatch(redirectToSignUp(GA_PASSWORDLESS));
};
export const sendPasswordlessEmail =
    ({ email, isDirect = false, ref }) =>
    (dispatch, getState) => {
        const state = getState();

        const { authentication: { referrer = ref } = {} } = state;
        dispatch(
            trackPasswordlessEmailAttempt({
                sourceProvider: GA_EVENTBRITE,
            }),
        );
        dispatch(clearAuthNotification());
        dispatch(
            setPasswordlessEmailStatus(PASSWORDLESS_EMAIL_STATUSES.ongoing),
        );
        dispatch(setSignupSecondStep(false));
        dispatch(clearEmail());
        return EmailsApi.sendPasswordlessEmail(email, referrer)
            .then(() => {
                dispatch(
                    setPasswordlessEmailStatus(
                        PASSWORDLESS_EMAIL_STATUSES.delivered,
                    ),
                );
                dispatch(
                    trackPasswordlessEmailSent({
                        sourceProvider: GA_EVENTBRITE,
                    }),
                );
            })
            .catch((error) => {
                const errorCode = get(
                    error,
                    'parsedError.error',
                    get(error, 'response.statusText', 'Error not defined'),
                );
                dispatch(
                    setPasswordlessEmailStatus(
                        PASSWORDLESS_EMAIL_STATUSES.failed,
                    ),
                );

                switch (errorCode) {
                    case 'USER_SUSPENDED':
                        dispatch(
                            showAuthNotification({
                                message: SUSPENDED_ACCOUNT_ERROR_MESSAGE,
                            }),
                        );

                        break;
                    case 'INVALID':
                        let iconType = null;
                        if (isDirect) {
                            // If user entered email in login form, then hide modal
                            dispatch(setShowPasswordlessModal(false));
                        } else {
                            // Display error on modal
                            dispatch(
                                updateSyncErrors(
                                    PASSWORDLESS_FORM,
                                    PASSWORDLESS_ERROR_SPEC[
                                        'EMAIL_CREATE_ACCOUNT'
                                    ],
                                ),
                            );
                            dispatch(touch(PASSWORDLESS_FORM, 'email'));
                            iconType = <AlertChunky />;
                        }
                        dispatch(
                            showAuthNotification({
                                message: NOT_EMAIL_ASSOCIATED,
                                onClick: redirectToSignUpWithLabel,
                                buttonCTA: CREATE_ACCOUNT,
                                iconType,
                            }),
                        );
                        dispatch(change(LOG_IN_FORM, 'email', email));
                        dispatch(setSignupSecondStep(true));
                        dispatch(setAccountExistent(email));
                        break;
                    case 'NOT_AUTHORIZED':
                        dispatch(
                            showAuthNotification({
                                message: NOT_AUTHORIZED_ERROR_MESSAGE,
                            }),
                        );

                        break;
                    case 'HIT_RATE_LIMIT':
                        dispatch(
                            setPasswordlessEmailStatus(
                                PASSWORDLESS_EMAIL_STATUSES.limitReached,
                            ),
                        );
                        break;
                    default:
                        dispatch(
                            showAuthNotification({
                                message: GENERIC_ERROR_MESSAGE,
                            }),
                        );
                        break;
                }

                dispatch(
                    trackPasswordlessEmailFailed({
                        errorMessage: errorCode,
                    }),
                );
            });
    };

export const passwordlessLoginAttempt =
    ({ token, isHardAccount, must_accept_tos = null, referrer = '/' }) =>
    (dispatch, getState) => {
        const state = getState();
        const {
            status: { tosSocialAccepted },
        } = state;

        let acceptedTosByPopup = 0;
        if (tosSocialAccepted && must_accept_tos == 'True') {
            acceptedTosByPopup = 1;
        }

        dispatch(setIsLoading(true));

        return PasswordlessApi.passwordlessLogin({
            internalToken: token,
            acceptedTosByPopup,
        })
            .then(() => {
                trackPasswordlessSuccess({
                    isHardAccount,
                    state,
                });

                trackLoginSuccessful({
                    sourceProvider: GA_PASSWORDLESS,
                    state,
                });
                HeapTrackingLibrary.trackLoginSuccessInHeap({
                    loginMethod: LOGIN_METHOD_PASSWORDLESS,
                });
                dispatch(submitSuccessRedirect(decodeURIComponent(referrer)));
            })
            .catch((error) => {
                const errorCode = get(
                    error,
                    'parsedError.error',
                    get(error, 'response.statusText', 'Error not defined'),
                );
                let notificationMessage = null;
                switch (errorCode) {
                    case 'ACCESS_DENIED': // Expired token
                        if (isHardAccount != 'True') {
                            // Only display notification in soft account
                            dispatch(
                                showAuthNotification({
                                    message: PASSWORDLESS_TOKEN_EXPIRED_MESSAGE,
                                    onClick: redirectToLogIn,
                                    buttonCTA: GO_TO_LOGIN,
                                }),
                            );
                        }
                        dispatch(
                            setPasswordlessLoginStatus(
                                PASSWORDLESS_LOGIN_TOKEN_EXPIRED,
                            ),
                        );
                        break;
                    case 'UNKNOWN_TOKEN': // Reused token
                        if (isHardAccount != 'True') {
                            // Only display notification in soft account
                            dispatch(
                                showAuthNotification({
                                    message: PASSWORDLESS_TOKEN_REUSED_MESSAGE,
                                    onClick: redirectToLogIn,
                                    buttonCTA: GO_TO_LOGIN,
                                }),
                            );
                        }
                        dispatch(
                            setPasswordlessLoginStatus(
                                PASSWORDLESS_LOGIN_TOKEN_REUSED,
                            ),
                        );
                        break;

                    case 'MUST_ACCEPT_TOS':
                        notificationMessage = TOS_PASSWORDLESS_MUST_BE_ACCEPTED;
                        dispatch(
                            setPasswordlessLoginStatus(
                                PASSWORDLESS_LOGIN_MUST_ACCEPT_TOS,
                            ),
                        );
                        break;
                    case 'NOT_AUTHORIZED':
                        notificationMessage = NOT_AUTHORIZED_ERROR_MESSAGE;

                        dispatch(
                            setPasswordlessLoginStatus(
                                PASSWORDLESS_LOGIN_NOT_AUTHORIZED,
                            ),
                        );
                        break;
                    default:
                        notificationMessage = GENERIC_ERROR_MESSAGE;

                        dispatch(
                            setPasswordlessLoginStatus(
                                PASSWORDLESS_LOGIN_OTHER_ERROR,
                            ),
                        );
                        break;
                }

                if (notificationMessage && isHardAccount != 'True') {
                    dispatch(
                        showAuthNotification({
                            message: notificationMessage,
                        }),
                    );
                }

                trackPasswordlessLoginFailed({
                    isHardAccount,
                    errorMessage: errorCode,
                    state,
                });
                dispatch(setIsLoading(false));
            });
    };

export const passwordlessSetPasswordAttempt =
    ({ token, must_accept_tos, password, referrer }) =>
    (dispatch, getState) => {
        const state = getState();
        const {
            status: { tosSocialAccepted },
        } = state;

        let acceptedTosByPopup = 0;
        if (tosSocialAccepted && must_accept_tos == 'True') {
            acceptedTosByPopup = 1;
        }

        dispatch(setIsLoading(true));

        return PasswordlessApi.passwordlessSetPassword({
            internalToken: token,
            acceptedTosByPopup,
            password,
        })
            .then(() => {
                trackPasswordlessSetPasswordSuccess({
                    state,
                });
                trackLoginSuccessful({
                    sourceProvider: GA_PASSWORDLESS,
                    state,
                });
                HeapTrackingLibrary.trackLoginSuccessInHeap({
                    loginMethod: LOGIN_METHOD_PASSWORDLESS,
                });

                dispatch(submitSuccessRedirect(decodeURIComponent(referrer)));
            })
            .catch((error) => {
                const errorCode = get(
                    error,
                    'parsedError.error',
                    get(error, 'response.statusText', 'Error not defined'),
                );
                let notificationMessage = null;
                switch (errorCode) {
                    case 'ACCESS_DENIED': // Expired token
                        dispatch(
                            showAuthNotification({
                                message: PASSWORDLESS_TOKEN_EXPIRED_MESSAGE,
                                onClick: redirectToLogIn,
                                buttonCTA: GO_TO_LOGIN,
                            }),
                        );

                        break;
                    case 'UNKNOWN_TOKEN': // Reused
                        dispatch(
                            showAuthNotification({
                                message: PASSWORDLESS_TOKEN_REUSED_MESSAGE,
                                onClick: redirectToLogIn,
                                buttonCTA: GO_TO_LOGIN,
                            }),
                        );

                        break;
                    case 'MUST_ACCEPT_TOS':
                        notificationMessage = TOS_PASSWORDLESS_MUST_BE_ACCEPTED;

                        break;
                    case 'NEW_PASSWORD_TOO_WEAK':
                        notificationMessage =
                            NEW_PASSWORD_TOO_WEAK_ERROR_MESSAGE;

                        break;
                    case 'USER_HAS_ALREADY_PASSWORD':
                        notificationMessage =
                            USER_HAS_ALREADY_PASSWORD_ERROR_MESSAGE;

                        break;
                    case 'USER_ACCOUNT_SUSPENDED':
                        notificationMessage =
                            USER_ACCOUNT_SUSPENDED_ERROR_MESSAGE;

                        break;
                    case 'INVALID_USER_ID':
                        notificationMessage = INVALID_USER_ID_ERROR_MESSAGE;

                        break;
                    case 'NOT_AUTHORIZED':
                        notificationMessage = NOT_AUTHORIZED_ERROR_MESSAGE;

                        break;
                    default:
                        notificationMessage = GENERIC_ERROR_MESSAGE;

                        break;
                }

                if (notificationMessage) {
                    dispatch(
                        showAuthNotification({
                            message: notificationMessage,
                        }),
                    );
                }

                trackPasswordlessSetPasswordFailed({
                    errorMessage: errorCode,
                    state,
                });
                dispatch(setIsLoading(false));
            });
    };

export const passwordlessEnterValidEmail = () => (dispatch, getState) => {
    dispatch(
        updateSyncErrors(LOG_IN_FORM, PASSWORDLESS_ERROR_SPEC['EMAIL_INVALID']),
    );
    dispatch(touch(LOG_IN_FORM, 'email'));
};
